// src/features/admin/components/settings/system-preferences/components/CalendarSettings.tsx
import React, { useState } from 'react';
import { CalendarIcon, ClockIcon, BellIcon } from '@heroicons/react/24/outline';

const MEETING_DURATIONS = [
  { value: 15, label: '15 minutes' },
  { value: 30, label: '30 minutes (Recommended)' },
  { value: 45, label: '45 minutes' },
  { value: 60, label: '1 hour' },
];

const REMINDER_TIMES = [
  { value: 5, label: '5 minutes before' },
  { value: 10, label: '10 minutes before' },
  { value: 15, label: '15 minutes before (Recommended)' },
  { value: 30, label: '30 minutes before' },
  { value: 60, label: '1 hour before' },
];

const HOLIDAY_CALENDARS = [
  { value: 'us-federal', label: 'US Federal Holidays', enabled: true },
  { value: 'us-state-ny', label: 'New York State Holidays', enabled: false },
  { value: 'us-state-ca', label: 'California State Holidays', enabled: false },
  { value: 'court-closures', label: 'Federal Court Closures', enabled: true },
];

const CalendarSettings: React.FC = () => {
  const [businessHoursStart, setBusinessHoursStart] = useState('09:00');
  const [businessHoursEnd, setBusinessHoursEnd] = useState('17:00');
  const [defaultMeetingDuration, setDefaultMeetingDuration] = useState(30);
  const [defaultReminder, setDefaultReminder] = useState(15);
  const [weekendSaturday, setWeekendSaturday] = useState(true);
  const [weekendSunday, setWeekendSunday] = useState(true);
  const [holidayCalendars, setHolidayCalendars] = useState(HOLIDAY_CALENDARS);

  const toggleHolidayCalendar = (value: string) => {
    setHolidayCalendars(prev =>
      prev.map(cal =>
        cal.value === value ? { ...cal, enabled: !cal.enabled } : cal
      )
    );
  };

  return (
    <div className="calendar-settings space-y-8">
      {/* Business Hours */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
        <div className="flex items-start mb-4">
          <ClockIcon className="h-6 w-6 text-lex-accent mr-3 mt-1" />
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-1">
              Business Hours
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Define your firm's standard working hours. This affects scheduling conflicts and availability.
            </p>
          </div>
        </div>

        <div className="ml-9 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Start Time
            </label>
            <input
              type="time"
              value={businessHoursStart}
              onChange={(e) => setBusinessHoursStart(e.target.value)}
              className="block w-full max-w-xs px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-lex-accent focus:border-lex-accent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              End Time
            </label>
            <input
              type="time"
              value={businessHoursEnd}
              onChange={(e) => setBusinessHoursEnd(e.target.value)}
              className="block w-full max-w-xs px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-lex-accent focus:border-lex-accent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
            />
          </div>

          <p className="text-xs text-gray-500 dark:text-gray-400">
            Business hours: {businessHoursStart} - {businessHoursEnd} ({
              ((parseInt(businessHoursEnd.split(':')[0]) - parseInt(businessHoursStart.split(':')[0])) || 8)
            } hours)
          </p>
        </div>
      </div>

      {/* Default Meeting Duration */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
        <div className="flex items-start mb-4">
          <CalendarIcon className="h-6 w-6 text-lex-accent mr-3 mt-1" />
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-1">
              Default Meeting Duration
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Set the default length for new calendar events and meetings.
            </p>
          </div>
        </div>

        <div className="ml-9">
          <select
            value={defaultMeetingDuration}
            onChange={(e) => setDefaultMeetingDuration(Number(e.target.value))}
            className="block w-full max-w-md px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-lex-accent focus:border-lex-accent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
          >
            {MEETING_DURATIONS.map(duration => (
              <option key={duration.value} value={duration.value}>{duration.label}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Reminder Defaults */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
        <div className="flex items-start mb-4">
          <BellIcon className="h-6 w-6 text-lex-accent mr-3 mt-1" />
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-1">
              Reminder Defaults
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Choose when to notify users about upcoming events and deadlines.
            </p>
          </div>
        </div>

        <div className="ml-9">
          <select
            value={defaultReminder}
            onChange={(e) => setDefaultReminder(Number(e.target.value))}
            className="block w-full max-w-md px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-lex-accent focus:border-lex-accent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
          >
            {REMINDER_TIMES.map(reminder => (
              <option key={reminder.value} value={reminder.value}>{reminder.label}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Weekend Definition */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
        <div className="flex items-start mb-4">
          <CalendarIcon className="h-6 w-6 text-lex-accent mr-3 mt-1" />
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-1">
              Weekend Definition
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Define which days are considered weekends. This affects deadline calculations and scheduling.
            </p>
          </div>
        </div>

        <div className="ml-9 space-y-3">
          <label className="flex items-center space-x-3">
            <input
              type="checkbox"
              checked={weekendSaturday}
              onChange={(e) => setWeekendSaturday(e.target.checked)}
              className="h-5 w-5 text-lex-accent focus:ring-lex-accent border-gray-300 rounded"
            />
            <span className="text-sm text-gray-900 dark:text-gray-100">Saturday is a weekend</span>
          </label>

          <label className="flex items-center space-x-3">
            <input
              type="checkbox"
              checked={weekendSunday}
              onChange={(e) => setWeekendSunday(e.target.checked)}
              className="h-5 w-5 text-lex-accent focus:ring-lex-accent border-gray-300 rounded"
            />
            <span className="text-sm text-gray-900 dark:text-gray-100">Sunday is a weekend</span>
          </label>

          <p className="text-xs text-gray-500 dark:text-gray-400 pt-2">
            💡 Tip: For international firms or those working with Middle Eastern jurisdictions, adjust weekend days accordingly.
          </p>
        </div>
      </div>

      {/* Holiday Calendar */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
        <div className="flex items-start mb-4">
          <CalendarIcon className="h-6 w-6 text-lex-accent mr-3 mt-1" />
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-1">
              Holiday Calendars
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Select which holiday calendars to observe. Holidays affect deadline calculations and court availability.
            </p>
          </div>
        </div>

        <div className="ml-9">
          <div className="space-y-3">
            {holidayCalendars.map(calendar => (
              <label
                key={calendar.value}
                className="flex items-center space-x-3 p-3 rounded-lg border border-gray-200 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer"
              >
                <input
                  type="checkbox"
                  checked={calendar.enabled}
                  onChange={() => toggleHolidayCalendar(calendar.value)}
                  className="h-5 w-5 text-lex-accent focus:ring-lex-accent border-gray-300 rounded"
                />
                <span className="text-sm text-gray-900 dark:text-gray-100">{calendar.label}</span>
              </label>
            ))}
          </div>

          <p className="mt-4 text-xs text-gray-500 dark:text-gray-400">
            📅 Selected holidays will be blocked out in the calendar and excluded from deadline calculations.
          </p>
        </div>
      </div>

      {/* Deadline Calculation Notice */}
      <div className="bg-lex-accent-soft dark:bg-lex-accent-soft border border-lex-accent dark:border-lex-accent rounded-md p-4">
        <div className="flex">
          <CalendarIcon className="h-5 w-5 text-lex-accent dark:text-lex-accent mt-0.5 mr-3 flex-shrink-0" />
          <div className="text-sm text-lex-accent dark:text-lex-accent">
            <p className="font-medium mb-1">Deadline Calculation</p>
            <p className="text-xs">
              Calendar settings directly affect how the system calculates legal deadlines. Weekend and holiday settings ensure compliance with court rules for filing deadlines.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CalendarSettings;

